import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The React binding: one provider that carries both halves of localisation.
 *
 *   the reader's language  → i18next, for every string
 *   the venue's locale     → Intl, for every amount, date and time
 *
 * They are separate on purpose. A member at a Bengaluru venue sees ₹ and Asia/Kolkata clock times;
 * the same member at a Dubai venue sees AED and Asia/Dubai — the venue decides money and time, never
 * the reader's device (spec/12 §7).
 */
import { createContext, useContext, useEffect, useMemo } from 'react';
import { I18nextProvider, initReactI18next, useTranslation } from 'react-i18next';
import { createI18n, resolveLocale } from './i18n.js';
import { createVenueFormatter } from './format.js';
const VenueFormatterContext = createContext(null);
export function ClavianI18nProvider({ venue, language, i18n, children, }) {
    const instance = useMemo(() => i18n ?? createI18n({ language: resolveLocale(language ?? venue.locale), use: [initReactI18next] }), [i18n, language, venue.locale]);
    const wanted = resolveLocale(language ?? venue.locale);
    useEffect(() => {
        if (instance.language !== wanted)
            void instance.changeLanguage(wanted);
    }, [instance, wanted]);
    const formatter = useMemo(() => createVenueFormatter(venue), [venue]);
    return (_jsx(I18nextProvider, { i18n: instance, children: _jsx(VenueFormatterContext.Provider, { value: formatter, children: children }) }));
}
/** The venue's Intl formatters. Throws outside the provider rather than guessing a timezone. */
export function useVenueFormatter() {
    const formatter = useContext(VenueFormatterContext);
    if (!formatter) {
        throw new Error('useVenueFormatter() needs a <ClavianI18nProvider venue={…}> above it — money and times follow the venue, so there is no sensible default.');
    }
    return formatter;
}
/** The component kit's own copy (the `ui` namespace). */
export function useUiText() {
    return useTranslation('ui').t;
}
/**
 * Where a refusal's words come from: the app's own `problems.*` in namespace `ns`, then the shared ones in `common`
 * (2026-10-12). Pass it to `problemView` with the facts about the failure. Money in a line (the fresh total of a
 * changed price) is formatted in the venue's currency when the provider is above; without one it is left out.
 */
export function useProblemCopy(ns) {
    const { t, i18n } = useTranslation([ns, 'common']);
    const formatter = useContext(VenueFormatterContext);
    return useMemo(() => ({
        line: (key, values) => {
            for (const namespace of ns === 'common' ? ['common'] : [ns, 'common']) {
                if (i18n.exists(`problems.${key}`, { ns: namespace }))
                    return t(`problems.${key}`, { ns: namespace, ...values });
            }
            return undefined;
        },
        ...(formatter === null ? {} : { money: (amount) => formatter.money(amount) }),
    }), [formatter, i18n, ns, t]);
}
export { useTranslation, initReactI18next };
//# sourceMappingURL=react.js.map